import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { FHButton, FHSelect, FHTimer } from '../../../../../components/base';
import { FORTA_BLUE, FORTA_SUCCESS, FORTA_WHITE } from '../../../../../utils/colors';
import { VBTSignatureMessage } from '../misc/SignatureMessages';
import { getParentEmailOptions, getGuardianVerificationDurationSeconds } from './utils/general';

import type { IBTSession, IProviderAssignment, SelectValueType } from '../../../../../utils/types';

interface VerifyParentSignatureProps {
  session: IBTSession;
  providerAssignments: IProviderAssignment[];
  selectedParentEmail: string;
  onParentEmailChange: (value: SelectValueType) => void;
  onVerifyIdentity: () => void;
  isGuardianVerificationLoading: boolean;
  isGuardianIdentityVerified: boolean;
  guardianVerificationExpiresAt: number | null;
}

function VerifyParentSignature({
  session,
  providerAssignments,
  selectedParentEmail,
  onParentEmailChange,
  onVerifyIdentity,
  isGuardianVerificationLoading,
  isGuardianIdentityVerified,
  guardianVerificationExpiresAt,
}: VerifyParentSignatureProps) {
  const parentEmailOptions = useMemo(
    () =>
      getParentEmailOptions({
        providerAssignments,
        sessionClientId: session.client_id,
        sessionStartDate: session.date_start,
      }),
    [providerAssignments, session.client_id, session.date_start],
  );

  const guardianVerificationDurationSeconds = useMemo(
    () =>
      getGuardianVerificationDurationSeconds({
        guardianVerificationExpiresAt,
        isGuardianIdentityVerified,
      }),
    [guardianVerificationExpiresAt, isGuardianIdentityVerified],
  );

  return (
    <Box>
      <Box sx={{ mb: 2, backgroundColor: FORTA_BLUE.light3, p: 2, borderRadius: 1 }}>
        <Typography>Parent email</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <FHSelect
            fullWidth
            options={parentEmailOptions}
            value={selectedParentEmail}
            onUpdate={onParentEmailChange}
          />
          <FHButton
            loading={isGuardianVerificationLoading}
            disabled={!selectedParentEmail}
            onClick={onVerifyIdentity}
          >
            Verify
          </FHButton>
        </Box>
        {isGuardianIdentityVerified && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              width: 'fit-content',
              p: 1,
              borderRadius: 1,
              backgroundColor: FORTA_WHITE,
            }}
          >
            <CheckCircleIcon sx={{ color: FORTA_SUCCESS }} />
            <Typography sx={{ color: FORTA_SUCCESS, fontWeight: 'bold' }}>
              Guardian identity verified.
            </Typography>
            {guardianVerificationDurationSeconds !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ color: FORTA_BLUE.primary, fontWeight: 'bold' }}>
                  Valid for
                </Typography>
                <FHTimer
                  key={guardianVerificationExpiresAt}
                  duration={guardianVerificationDurationSeconds}
                  sx={{ color: FORTA_BLUE.primary, fontWeight: 'bold' }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box>
        <VBTSignatureMessage />
      </Box>
    </Box>
  );
}

export default VerifyParentSignature;
